// Considered: AssigneeMultiSelectorMolecule — rejected because it is multi-select and writes an ASSIGNED_TO edge; a reference is single-select and writes a plain property id. See spec Reuse Audit.
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { SchemaProperty } from 'types/schema';
import type { FilterExpression, PerspectiveFilter, UnifiedFilters } from 'types/filters';
import { useSphereSearch } from '../../../hooks/useSphereSearch';
import { useSphereInfo } from '../../../hooks/useSphereInfo';
import DropdownAtom, { DropdownChangeEvent } from '../../atoms/Dropdown/DropdownAtom';
import { resolveValuePlaceholders, extractValuePlaceholderDeps } from './resolveValuePlaceholders';
import './SphereReferenceInputMolecule.scss';

export interface SphereReferenceInputMoleculeProps {
  property: SchemaProperty;
  value: string | null;
  onChange: (id: string | null) => void;
  disabled?: boolean;
  editable?: boolean;
  className?: string;
  /** Current values of sibling properties — used to resolve $value(<sibling>) placeholders in targetFilter. */
  siblingValues?: Record<string, any>;
  /**
   * Org the sphere being edited belongs to. Candidate references are resolved in
   * this org (not the caller's home org), so reference data seeded into a deployed
   * org is found when an admin edits there. Omit to search the caller's own org.
   */
  organizationId?: string;
  /** Accessible name for the dropdown — this molecule renders no visible `<label>` of its own. */
  ariaLabel?: string;
}

const SphereReferenceInputMolecule: React.FC<SphereReferenceInputMoleculeProps> = ({
  property,
  value,
  onChange,
  disabled = false,
  editable = true,
  className = '',
  siblingValues,
  organizationId,
  ariaLabel,
}) => {
  const { t } = useTranslation();

  const meta = (property as any).metadata as {
    targetSchemaSystemName?: string;
    displayProperty?: string;
    targetFilter?: UnifiedFilters;
  } | undefined;

  const targetSchemaSystemName = meta?.targetSchemaSystemName ?? '';
  const targetFilter = meta?.targetFilter;

  // Only the sibling values referenced by $value() placeholders affect the resolved
  // filter. Key the resolve-memo on those, not the whole siblingValues object (which
  // the parent recreates on every render) — otherwise an unrelated edit (e.g. clicking
  // an integer field's +/- stepper) rebuilds filterExpressions by reference and
  // re-fires the /search query on every click.
  const valueDeps = useMemo(() => extractValuePlaceholderDeps(targetFilter), [targetFilter]);
  const depsKey = valueDeps.map((d) => JSON.stringify(siblingValues?.[d] ?? null)).join('|');

  // When a $value(<sibling>) placeholder can't be resolved (the sibling field is
  // still unset), the dependent reference has no valid candidate set — block the
  // query and disable the input rather than querying with an unresolved literal.
  const { filterExpressions, perspectiveFilters, blocked } = useMemo((): {
    filterExpressions: FilterExpression[];
    perspectiveFilters: PerspectiveFilter[];
    blocked: boolean;
  } => {
    const base: FilterExpression[] = [
      { property: 'type', operator: 'equals', value: targetSchemaSystemName },
    ];

    if (!targetFilter?.propertyFilters?.length && !targetFilter?.perspectiveFilters?.length) {
      return { filterExpressions: base, perspectiveFilters: [], blocked: false };
    }

    const { resolved, unresolvedSiblings } = resolveValuePlaceholders(targetFilter, siblingValues ?? {});
    if (unresolvedSiblings.length > 0) {
      return { filterExpressions: base, perspectiveFilters: [], blocked: true };
    }
    return {
      filterExpressions: [...base, ...(resolved.propertyFilters ?? [])],
      perspectiveFilters: resolved.perspectiveFilters ?? [],
      blocked: false,
    };
    // siblingValues is read via depsKey; only $value() deps should retrigger the
    // resolve (react-hooks/exhaustive-deps is not enforced in design-system).
  }, [targetSchemaSystemName, targetFilter, depsKey]);

  const { results, loading, setQuery } = useSphereSearch({
    filterExpressions,
    perspectiveFilters: perspectiveFilters.length > 0 ? perspectiveFilters : undefined,
    permission: 'view',
    pageSize: 25,
    enabled: !blocked,
    targetOrganizationId: organizationId,
  });

  const { info: sphereInfo } = useSphereInfo(value ?? undefined);

  // `label` on a search result is the Gremlin vertex label (e.g. "sphere"); the
  // human-readable name lives in `properties.name` (matches the canonical
  // `properties?.name || label` read used across sphere pickers).
  const options = useMemo(
    () => results.map((r) => ({ label: r.properties?.name || r.label, id: r.id })),
    [results]
  );

  if (!editable) {
    const displayProperty = meta?.displayProperty ?? 'name';
    const displayName =
      displayProperty === 'name'
        ? (sphereInfo?.properties?.name ?? sphereInfo?.label ?? value ?? '')
        : value ?? '';
    return (
      <span className={`sphere-reference-input__display ${className}`.trim()}>
        {displayName || '—'}
      </span>
    );
  }

  return (
    <DropdownAtom
      value={value}
      onChange={(e: DropdownChangeEvent) => onChange(e.value ?? null)}
      options={options}
      optionLabel="label"
      optionValue="id"
      filter
      onFilter={(e) => setQuery(e.filter)}
      loading={loading}
      disabled={disabled || blocked}
      ariaLabel={ariaLabel}
      showClear
      className={`sphere-reference-input__dropdown w-full ${className}`.trim()}
      placeholder={
        blocked
          ? t('sphere.property.reference.selectDependencyFirst')
          : t('sphere.property.reference.searchPlaceholder')
      }
      emptyMessage={
        loading
          ? t('sphere.property.reference.loading')
          : t('sphere.property.reference.noResults')
      }
    />
  );
};

export default SphereReferenceInputMolecule;
